import { hasMeetingLocation } from "../../../utils/meetingLocationSelection";
import MeetingLocationPicker from "../../../components/meetingLocation/MeetingLocationPicker";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Heart,
  X,
  Loader2,
  ChevronDown,
  MapPin,
} from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import apiClient from "../../../api/apiClient";
import TopNavBar from "../../../components/TopNavBar";
import { useGlobalAlert } from "../../../context/AlertContext";
import { format, isToday, isTomorrow } from "date-fns";
import dayjs from "dayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useNotifications } from "../../../context/NotificationContext";
import userPlaceholder from "../../../assets/userPlaceholder.png";
import dogPlaceholder from "../../../assets/dogPlaceholder.png";
import appIcon from "../../../assets/appIcon.svg";
import "./ReceiveABookPage.css";
import MobileTopBar from "../../../components/MobileTopBar";

import {
  doc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase-config";

const formatFirstName = (name) => {
  if (!name || typeof name !== "string") return "";
  const firstName = name.trim().split(" ")[0];
  if (!firstName) return "";
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
};

const parseWalkDate = (dateString) => {
  if (!dateString) return null;
  const formattedString =
    typeof dateString === "string" ? dateString.replace(" ", "T") : dateString;
  const dateObj = new Date(formattedString);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 3958.8;
  const rLat1 = lat1 * (Math.PI / 180);
  const rLat2 = lat2 * (Math.PI / 180);
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const formatDistance = (miles = 0) => {
  if (!miles || miles === 0) return "";
  const roundedMiles = Number(miles.toFixed(1));
  return `${roundedMiles} ${roundedMiles === 1 ? "mi" : "mi"} away`;
};

const ReceiveABookPage = () => {
  const navigate = useNavigate();
  const { setUnreadCount } = useNotifications();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [activeNotificationId, setActiveNotificationId] = useState(null);

  const timeDropdownRef = useRef(null);

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [showBookWalkModal, setShowBookWalkModal] = useState(false);
  const [showWalkConfirmModal, setShowWalkConfirmModal] = useState(false);
  const [showMeetingPointModal, setShowMeetingPointModal] = useState(false);
  const [showRescheduleConfirmModal, setShowRescheduleConfirmModal] =
    useState(false);
  const [showIncompleteProfileModal, setShowIncompleteProfileModal] =
    useState(false);

  const [value, setValue] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [walkMessage, setWalkMessage] = useState("");

  const [selectedMeetingAddress, setSelectedMeetingAddress] = useState("");
  const [selectedMeetingLocation, setSelectedMeetingLocation] = useState(null);
  const [userLocation, setUserLocation] = useState({
    lat: 40.7648,
    lng: -73.9808,
  });

  const [currentMapCoords, setCurrentMapCoords] = useState({
    lat: 40.7648,
    lng: -73.9808,
  });

  useEffect(() => {
    const urlId = searchParams.get("notificationId");
    const stateId = location.state?.notificationId;
    const finalId = urlId || stateId;

    if (finalId) {
      setActiveNotificationId(finalId);
      if (urlId) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      } else if (stateId) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [
    searchParams,
    location.state?.notificationId,
    location.pathname,
    navigate,
  ]);

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get(`/notifications/${profile?.id}`);
      if (response.data.status) {
        const data = response.data.data;
        const count = data.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
    }
  }, [profile?.id]);

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatWalkTime = (dateString) => {
    if (!dateString) return "Not Specified";
    const date = parseWalkDate(dateString);
    if (!date || isNaN(date.getTime())) return "Not Specified";
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const part1 = format(date, "EEE, MMM d");
    const part2 = format(date, "h:mm a");
    return `${part1}${suffix} at ${part2}`;
  };

  useEffect(() => {
    const fetchNotificationDetails = async () => {
      if (!activeNotificationId) return;

      setLoading(true);
      try {
        const response = await apiClient.get(
          `/notifications/get-notification-ById-for-bookwalk/${activeNotificationId}`,
        );

        if (response.data.status && response.data.data.length > 0) {
          const rawData = response.data.data[0];

          let distanceMiles = 0;
          const savedLocStr = localStorage.getItem("user_location_coords");
          if (savedLocStr && rawData.user_latitude && rawData.user_longitude) {
            try {
              const savedLoc = JSON.parse(savedLocStr);
              distanceMiles = calculateDistanceMiles(
                savedLoc.lat,
                savedLoc.lng,
                parseFloat(rawData.user_latitude),
                parseFloat(rawData.user_longitude),
              );
            } catch (e) {
              console.error("Failed distance calc", e);
            }
          }

          const lat = parseFloat(
            rawData.meeting_lat ||
              rawData.user_latitude ||
              rawData.latitude ||
              40.7648,
          );
          const lng = parseFloat(
            rawData.meeting_lng ||
              rawData.user_longitude ||
              rawData.longitude ||
              -73.9808,
          );
          setCurrentMapCoords({ lat, lng });
          setUserLocation({ lat, lng });

          const isReceiverUser = profile?.id === rawData.receiver_user_id;

          let partnerMessage = "";
          if (isReceiverUser) {
            partnerMessage = rawData.sender_offer_message || "";
          } else {
            partnerMessage = rawData.receiver_offer_message || "";
          }

          setBookingData({
            ...rawData,
            formatted_date: formatWalkTime(rawData.booking_date),
            full_address: rawData.meeting_location || "Meeting Point",
            booking_id: rawData.Booking_WalkId || rawData.walk_id || rawData.id,
            sender_user_name: formatFirstName(rawData.sender_user_name || ""),
            sender_dog_name: formatFirstName(rawData.sender_dog_name || ""),
            receiver_user_name: formatFirstName(
              rawData.receiver_user_name || "",
            ),
            receiver_dog_name: formatFirstName(rawData.receiver_dog_name || ""),
            sender_profile_image: rawData.sender_profile_image,
            sender_dog_images: rawData.sender_dog_images,
            receiver_profile_image: rawData.receiver_profile_image,
            receiver_dog_images: rawData.receiver_dog_images,
            sender_offer_message: rawData.sender_offer_message || "",
            receiver_offer_message: rawData.receiver_offer_message || "",
            partnerMessage: partnerMessage,
            distance_miles: distanceMiles,
          });
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        triggerAlert("Could not load request details", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationDetails();
  }, [activeNotificationId, location.state?.isWalkRequest, profile?.id]);

  useEffect(() => {
    if (
      isMapLoaded &&
      bookingData &&
      (!bookingData.meeting_lat || !bookingData.meeting_lng)
    ) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address: bookingData.full_address },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            setCurrentMapCoords({ lat: loc.lat(), lng: loc.lng() });
            setUserLocation({ lat: loc.lat(), lng: loc.lng() });
          }
        },
      );
    }
  }, [isMapLoaded, bookingData]);

  const checkCompletionStatus = () => {
    const isComplete = profile?.dogs?.[0]?.isDogProfileComplete;
    const isHalfComplete = profile?.dogs?.[0]?.isDogProfileHalfComplete;

    if (isComplete === 1) return true;
    if (isComplete === 0 && isHalfComplete === 1) {
      navigate("/half-completed-page");
      return false;
    }
    setShowIncompleteProfileModal(true);
    return false;
  };

  const TIME_SLOTS = useMemo(() => {
    const interval = 30;
    const slots = [];
    const now = dayjs();
    const isTodayVal = value && value.isSame(now, "day");

    const currentMinute = now.minute();
    const addMinutes = interval - (currentMinute % interval);
    const nextAvailableTime = now
      .clone()
      .add(addMinutes, "minute")
      .second(0)
      .millisecond(0);

    for (let h = 6; h <= 21; h++) {
      for (let m = 0; m < 60; m += interval) {
        if (h === 21 && m > 0) break;
        const slotTime = (value || now)
          .clone()
          .hour(h)
          .minute(m)
          .second(0)
          .millisecond(0);

        if (isTodayVal) {
          if (
            slotTime.isSame(nextAvailableTime) ||
            slotTime.isAfter(nextAvailableTime)
          ) {
            slots.push(slotTime.format("hh:mm A"));
          }
        } else {
          slots.push(slotTime.format("hh:mm A"));
        }
      }
    }
    return slots;
  }, [value]);

  const resetToInitial = () => {
    if (bookingData?.booking_date) {
      const parsed = parseWalkDate(bookingData.booking_date);
      const d = dayjs(parsed);
      setValue(d);
      setSelectedTime(d.format("hh:mm A"));
    } else {
      setValue(dayjs());
      setSelectedTime(null);
    }
    setSelectedMeetingAddress(bookingData?.full_address || "");
    setSelectedMeetingLocation(currentMapCoords);
    setWalkMessage("");
  };

  const handleCloseAllModals = () => {
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(false);
    setShowRescheduleConfirmModal(false);
    resetToInitial();
  };

  const handleRescheduleClick = () => {
    if (!checkCompletionStatus()) return;
    if (!bookingData) return;
    resetToInitial();
    setShowBookWalkModal(true);
  };

  const handleProceedToMessage = () => {
    if (!selectedTime) return triggerAlert("Please select a time", "error");
    const [time, meridian] = selectedTime.split(" ");
    const [hour, minute] = time.split(":").map(Number);
    const finalDateTime = value
      .hour(
        meridian === "PM" && hour !== 12
          ? hour + 12
          : hour === 12 && meridian === "AM"
            ? 0
            : hour,
      )
      .minute(minute)
      .second(0);
    setSelectedDate(finalDateTime.toDate());

    setShowBookWalkModal(false);
    setShowWalkConfirmModal(true);
  };

  const handleProceedToLocation = () => {
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const handleProceedToFinalConfirm = () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      return triggerAlert("Please select a meeting location", "error");
    }
    setShowMeetingPointModal(false);
    setShowRescheduleConfirmModal(true);
  };

  const handleAcceptSubmit = async () => {
    if (!checkCompletionStatus()) return;
    if (!bookingData?.booking_id) return;
    setIsRescheduling(true);

    try {
      const payload = { status: "approved" };
      const res = await apiClient.put(
        `/bookwalks/update-walk-byId/${bookingData.booking_id}`,
        payload,
      );
      if (res.data?.status) {
        try {
          const p1 = String(bookingData.sender_user_id);
          const p2 = String(bookingData.receiver_user_id);
          const sortedIds = [p1, p2].sort();
          const unifiedChatId = `${sortedIds[0]}_${sortedIds[1]}`;

          const chatRef = doc(db, "web_chat_list", unifiedChatId);
          const messagesRef = collection(chatRef, "messages");
          const q = query(
            messagesRef,
            where("walk_id", "==", bookingData.booking_id),
          );
          const querySnapshot = await getDocs(q);

          const updatePromises = querySnapshot.docs.map((docSnap) =>
            updateDoc(docSnap.ref, {
              message: "walk booked successfully",
              walk_status: "approved",
            }),
          );
          await Promise.all(updatePromises);
        } catch (fsError) {
          console.error("Firestore message update error:", fsError);
        }

        triggerAlert("Walk accepted successfully!", "success");
        handleCloseAllModals();
        navigate("/current-bookings");
      }
    } catch (err) {
      triggerAlert("Failed to accept walk.", "error");
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    if (!bookingData?.booking_id) return;
    setIsRescheduling(true);

    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isReceiverUser = profile?.id === bookingData?.receiver_user_id;
      const defaultMsg =
        walkMessage || "Suggested a new time and location for our walk!";

      const payload = {
        date: selectedDate
          ? selectedDate.toISOString().slice(0, 19).replace("T", " ")
          : null,
        timezone: deviceTimezone,
        sender_user_id: profile?.id,
        meeting_location: selectedMeetingAddress.trim(),
        meeting_lat: selectedMeetingLocation?.lat ?? currentMapCoords.lat,
        meeting_lng: selectedMeetingLocation?.lng ?? currentMapCoords.lng,
        sender_offer_message: !isReceiverUser
          ? defaultMsg
          : bookingData?.sender_offer_message || "",
        receiver_offer_message: isReceiverUser
          ? defaultMsg
          : bookingData?.receiver_offer_message || "",
      };

      const res = await apiClient.put(
        `/bookwalks/walk-date-update-byId/${bookingData.booking_id}`,
        payload,
      );

      if (res.data?.status) {
        try {
          const currentUserId = String(profile.id);
          const partnerId = isReceiverUser
            ? String(bookingData.sender_user_id)
            : String(bookingData.receiver_user_id);
          const p1 = String(bookingData.sender_user_id);
          const p2 = String(bookingData.receiver_user_id);
          const sortedIds = [p1, p2].sort();
          const chatDocId = `${sortedIds[0]}_${sortedIds[1]}`;

          if (defaultMsg && defaultMsg.trim() !== "") {
            const chatRef = doc(db, "web_chat_list", chatDocId);
            const messagesRef = collection(chatRef, "messages");
            const newMsgRef = doc(messagesRef);

            await setDoc(
              chatRef,
              {
                users: [p1, p2],
                last_message: defaultMsg,
                last_timestamp: serverTimestamp(),
              },
              { merge: true },
            );

            await setDoc(newMsgRef, {
              sender_id: currentUserId,
              receiver_id: partnerId,
              message: defaultMsg,
              file: null,
              timestamp: serverTimestamp(),
              is_read: false,
            });
          }
        } catch (chatErr) {
          console.error("Failed to add walk message to chat:", chatErr);
        }

        triggerAlert(
          "Suggested time and location updated successfully!",
          "success",
        );
        handleCloseAllModals();
        navigate("/current-bookings");
      }
    } catch (err) {
      triggerAlert("Failed to update walk suggestion.", "error");
    } finally {
      setIsRescheduling(false);
    }
  };

  const isSender =
    profile?.id ===
    (bookingData?.requester_user_id || bookingData?.sender_user_id);
  const partnerName = formatFirstName(
    isSender
      ? bookingData?.receiver_user_name ||
          bookingData?.receiver_dog_name ||
          "Partner"
      : bookingData?.sender_user_name ||
          bookingData?.sender_dog_name ||
          "Partner",
  );
  const partnerDogName = formatFirstName(
    isSender ? bookingData?.receiver_dog_name : bookingData?.sender_dog_name,
  );
  const myDogName = formatFirstName(
    isSender ? bookingData?.sender_dog_name : bookingData?.receiver_dog_name,
  );

  const partnerImage = isSender
    ? bookingData?.receiver_profile_image?.[0] ||
      bookingData?.receiver_dog_images?.[0]
    : bookingData?.sender_profile_image?.[0] ||
      bookingData?.sender_dog_images?.[0];

  const handleScrollToTime = () => {
    const isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
    if (!isMobile) return;
    setShowTimeDropdown(true);
    setTimeout(() => {
      timeDropdownRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const handleCalendarClick = (e) => {
    if (e.target.closest(".MuiPickersDay-root")) {
      handleScrollToTime();
    }
  };

  const { locationTitle, locationSubtitle } = useMemo(() => {
    const addr = bookingData?.full_address || "Meeting Location";
    const parts = addr.split(",");
    if (parts.length > 1) {
      return {
        locationTitle: parts[0].trim(),
        locationSubtitle: parts.slice(1).join(",").trim(),
      };
    }
    return { locationTitle: addr, locationSubtitle: "Meeting Location" };
  }, [bookingData?.full_address]);

  const { dateLabel, dateFormatted, timeLabel, timeFormatted } = useMemo(() => {
    if (!bookingData?.booking_date) {
      const avail = bookingData?.availability?.length
        ? bookingData.availability.join(", ")
        : "Flexible";
      return {
        dateLabel: "AVAILABILITY",
        dateFormatted: "Flexible Schedule",
        timeLabel: "PREFERRED",
        timeFormatted: avail,
      };
    }

    const d = parseWalkDate(bookingData.booking_date);
    if (!d || isNaN(d.getTime())) {
      return {
        dateLabel: "DATE",
        dateFormatted: "Invalid Date",
        timeLabel: "TIME",
        timeFormatted: "Invalid Time",
      };
    }

    let dLabel = "DATE";
    if (isToday(d)) dLabel = "TODAY";
    else if (isTomorrow(d)) dLabel = "TOMORROW";

    const dayNum = d.getDate();
    const dFormatted = format(d, `EEE, MMM d'${getOrdinalSuffix(dayNum)}'`);

    const hour = d.getHours();
    let tLabel = "TIME";
    if (hour < 12) tLabel = "MORNING";
    else if (hour < 17) tLabel = "AFTERNOON";
    else tLabel = "EVENING";

    const tFormatted = format(d, "h:mm a");
    return {
      dateLabel: dLabel,
      dateFormatted: dFormatted,
      timeLabel: tLabel,
      timeFormatted: tFormatted,
    };
  }, [bookingData?.booking_date, bookingData?.availability]);

  if (loading) {
    return (
      <div className="rab-loading-screen">
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="rab-container">
      {/* Desktop Top Header */}
      <div
        className="msg-custom-top-header notif-desktop-only"
        style={{ padding: "2rem" }}
      >
        <div
          className="notif-ui-navbar-div"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            marginTop: "0",
          }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span style={{ color: "#fff", fontSize: "22px", fontWeight: "700" }}>
            Social Paws
          </span>
        </div>
        <div className="notif-ui-nav-icons">
          <TopNavBar theme="dark" />
        </div>
      </div>

      {/* Mobile Top Header */}
      <div className="notif-ui-navbar-mobile notif-mobile-only">
        <MobileTopBar
          onBack={() => navigate("/dashboard-switcher")}
          backColor="#fff"
          logoColor="#fff"
        />
        <div className="notif-mobile-header-bottom">
          <div className="notif-ui-nav-icons">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      {/* CENTER CONTENT */}
      <div className="rab-content-wrapper">
        <div className="rab-panel">
          <div className="rab-card">
            <button
              className="rab-card-close-btn"
              onClick={() => navigate("/dashboard-switcher")}
            >
              <X size={18} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            {/* DYNAMIC TOP TITLE */}
            <h1 className="rab-top-text">
              {partnerDogName
                ? `${partnerName} would like to Offer A Walk with You & ${myDogName || "your pup"}!`
                : `${partnerName} would like to Offer A Walk with You!`}
            </h1>

            {/* SENDER INFO & MESSAGE BOX */}
            <div className="rab-sender-info-box">
              <div className="rab-sender-photo-wrapper">
                <img src={partnerImage || userPlaceholder} alt={partnerName} />
                <div className="rab-sender-photo-overlay">
                  <Heart size={16} fill="#fff" color="#fff" />
                  <span className="sender-name">{partnerName}</span>
                  {/* <span className="sender-distance">
                    {formatDistance(bookingData?.distance_miles)}
                  </span> */}
                </div>
              </div>
              <div className="rab-sender-quote-box">
                <p className="rab-sender-quote-name">{partnerName} says,</p>
                <p className="rab-sender-quote-text">
                  "
                  {bookingData?.partnerMessage ||
                    "I would love to meet up and go for a walk together!"}
                  "
                </p>
              </div>
            </div>

            {/* WALK DETAILS SECTION */}
            <div className="rab-walk-details-section">
              <h2 className="rab-walk-details-heading">Walk details</h2>
              <div className="rab-walk-details-card">
                {/* Map Preview */}
                <div className="rab-map-preview-wrapper">
                  <div className="rab-group-walk-badge">
                    <span className="green-dot">●</span> GROUP WALK
                  </div>
                  {isMapLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "16px",
                      }}
                      center={currentMapCoords}
                      zoom={15}
                      options={{
                        disableDefaultUI: true,
                        gestureHandling: "none",
                        zoomControl: false,
                      }}
                    >
                      <Marker position={currentMapCoords} />
                    </GoogleMap>
                  ) : (
                    <div className="rab-map-fallback">
                      <Loader2 className="animate-spin" size={24} />
                      <span>Loading Map...</span>
                    </div>
                  )}
                </div>

                {/* Location Title & Address */}
                <div className="rab-location-info">
                  <h3 className="rab-loc-title">{locationTitle}</h3>
                  <div className="rab-loc-address-row">
                    <MapPin size={18} className="rab-pin-icon" />
                    <span>{locationSubtitle}</span>
                  </div>
                </div>

                {/* Date & Time Pills */}
                <div className="rab-time-pills-row">
                  <div className="rab-time-pill date-pill">
                    <span className="pill-label">{dateLabel}</span>
                    <span className="pill-value">{dateFormatted}</span>
                  </div>
                  <div className="rab-time-pill time-pill">
                    <span className="pill-label">{timeLabel}</span>
                    <span className="pill-value">{timeFormatted}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="rab-action-btns">
              <button
                className={`rab-btn rab-btn-primary ${isRescheduling ? "disabled" : ""}`}
                onClick={handleAcceptSubmit}
                disabled={isRescheduling}
              >
                {isRescheduling ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  "ACCEPT TIME & LOCATION"
                )}
              </button>

              <button
                className="rab-btn rab-btn-secondary"
                onClick={handleRescheduleClick}
                disabled={isRescheduling}
              >
                SUGGEST ANOTHER TIME OR LOCATION
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* INCOMPLETE PROFILE MODAL */}
      {showIncompleteProfileModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div
            className="book-modal-card"
            style={{
              padding: "1.5rem",
              textAlign: "center",
              maxWidth: "400px",
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowIncompleteProfileModal(false)}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <h2
              className="book-title"
              style={{ margin: "1rem 0", fontSize: "1rem" }}
            >
              Want to interact with the community? Complete your profile first!
              🐾
            </h2>
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="rab-btn rab-btn-secondary"
                style={{ padding: "10px 30px" }}
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  navigate("/team-selection", {
                    state: { routePath: "checkCompletionStatus" },
                  });
                }}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}

      {showBookWalkModal && bookingData && (
        <div className="rab-book-modal-overlay">
          <div className="rab-book-modal-card">
            <button
              className="rab-modal-close-btn"
              onClick={handleCloseAllModals}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="rab-tab-content-calendar">
              <h2 className="rab-book-title">When would you like to walk?</h2>
              <p className="rab-book-subtitle">Offer a day & time</p>

              <div className="rab-calendar-layout">
                <div className="rab-custom-calendar-container">
                  <div className="rab-cal-selected-display">
                    <h2>
                      {value ? format(value, "EEE, MMM d") : "Select a date"}
                    </h2>
                  </div>
                  <div className="rdp-wrapper-purple">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box
                        sx={{ width: "100%", maxWidth: 360 }}
                        onClick={handleCalendarClick}
                      >
                        <DateCalendar
                          value={value}
                          disablePast
                          onChange={(newValue) => {
                            setValue(newValue);
                            setSelectedTime(null);
                            handleScrollToTime();
                          }}
                        />
                      </Box>
                    </LocalizationProvider>
                  </div>
                </div>

                <div className="time-selector-area" ref={timeDropdownRef}>
                  <label>TIME</label>
                  <div
                    className="custom-dropdown"
                    style={{
                      backgroundColor: selectedTime
                        ? "#fff"
                        : "rgba(182, 252, 238, 1)",
                    }}
                    onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  >
                    <span>{selectedTime || "Choose a time"}</span>
                    <ChevronDown size={16} />
                  </div>
                  {showTimeDropdown && (
                    <div className="time-dropdown-list">
                      {TIME_SLOTS.map((time) => (
                        <div
                          key={time}
                          className={`time-option ${selectedTime === time ? "active" : ""}`}
                          onClick={() => {
                            setSelectedTime(time);
                            setShowTimeDropdown(false);
                          }}
                        >
                          {time}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="rab-book-modal-next-container">
                    <button
                      className={`rab-book-modal-next-btn ${!selectedTime ? "disabled" : ""}`}
                      onClick={handleProceedToMessage}
                      disabled={!selectedTime}
                    >
                      SUBMIT
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWalkConfirmModal && (
        <div className="rab-book-modal-overlay">
          <div className="rab-location-modal-card">
            <div className="rab-modal-top-header">
              <button
                className="rab-modal-back-btn"
                onClick={() => {
                  setShowWalkConfirmModal(false);
                  setShowBookWalkModal(true);
                }}
              >
                <ArrowLeft size={20} color="#000" strokeWidth={3} />
                <span>Back</span>
              </button>
              <button
                className="rab-modal-close-btn"
                onClick={handleCloseAllModals}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="rab-location-modal-content">
              <h2 className="rab-loc-modal-title">
                You would like to walk with {partnerDogName || "Dog"} &{" "}
                {partnerName} on{" "}
                {selectedDate ? formatWalkTime(selectedDate) : ""}
              </h2>
              <p
                className="rab-loc-modal-subtitle"
                style={{ marginBottom: "16px" }}
              >
                Personalize your offer with a message
              </p>

              <textarea
                className="rab-reschedule-note-input"
                style={{ height: "180px" }}
                placeholder="Write here"
                value={walkMessage}
                onChange={(e) => setWalkMessage(e.target.value)}
              />

              <button
                className="rab-loc-confirm-btn"
                onClick={handleProceedToLocation}
              >
                CONFIRM
              </button>

              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#000",
                  marginTop: "16px",
                  textAlign: "center",
                  lineHeight: "1.35",
                }}
              >
                We will reach out to {partnerName} with your suggested time and
                find the best time for your first group walk to either confirm
                or suggest another time or place. check your bookings tab to see
                more!
              </p>
            </div>
          </div>
        </div>
      )}

      {showMeetingPointModal && (
        <div className="rab-book-modal-overlay">
          <div className="rab-location-modal-card">
            <div className="rab-modal-top-header">
              <button
                className="rab-modal-back-btn"
                onClick={() => {
                  setShowMeetingPointModal(false);
                  setShowWalkConfirmModal(true);
                }}
              >
                <ArrowLeft size={20} color="#000" strokeWidth={3} />
                <span>Back</span>
              </button>
              <button
                className="rab-modal-close-btn"
                onClick={handleCloseAllModals}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="rab-location-modal-content">
              <h2 className="rab-loc-modal-title">SELECT MEETING LOCATION</h2>
              <p className="rab-loc-modal-subtitle">
                Choose from common locations or tap on map to pin a location on
                map
              </p>

              <MeetingLocationPicker
                profile={profile}
                partnerId={String(bookingData?.requester_user_id ?? bookingData?.sender_user_id) === String(profile?.id) ? (bookingData?.walker_user_id ?? bookingData?.receiver_user_id) : (bookingData?.requester_user_id ?? bookingData?.sender_user_id)}
                dogId={bookingData?.dog_id}
                isMapLoaded={isMapLoaded}
                initialCenter={userLocation}
                selectedLocation={selectedMeetingLocation}
                selectedAddress={selectedMeetingAddress}
                onSelect={(place) => {
                  setSelectedMeetingLocation(place);
                  setSelectedMeetingAddress(place.address || place.name);
                }}
              />

              <button
                className="rab-loc-confirm-btn"
                disabled={!hasMeetingLocation(selectedMeetingAddress)}
                onClick={handleProceedToFinalConfirm}
              >
                CONFIRM
              </button>
            </div>
          </div>
        </div>
      )}

      {showRescheduleConfirmModal && (
        <div className="rab-book-modal-overlay">
          <div className="rab-location-modal-card">
            <div className="rab-modal-top-header">
              <button
                className="rab-modal-back-btn"
                onClick={() => {
                  setShowRescheduleConfirmModal(false);
                  setShowMeetingPointModal(true);
                }}
              >
                <ArrowLeft size={20} color="#000" strokeWidth={3} />
                <span>Back</span>
              </button>
              <button
                className="rab-modal-close-btn"
                onClick={handleCloseAllModals}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="rab-location-modal-content">
              <h2 className="rab-loc-modal-title">
                Your Meeting point will be:
              </h2>
              <h3 className="rab-loc-modal-highlight">
                {selectedMeetingAddress || "Dewitt Clinton Park Dog Run"}
              </h3>

              <div
                className="rab-map-interactive-container"
                style={{ height: "240px", margin: "1rem 0" }}
              >
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "16px",
                    }}
                    center={{
                      lat: selectedMeetingLocation?.lat
                        ? parseFloat(selectedMeetingLocation.lat)
                        : parseFloat(userLocation.lat),
                      lng: selectedMeetingLocation?.lng
                        ? parseFloat(selectedMeetingLocation.lng)
                        : parseFloat(userLocation.lng),
                    }}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      gestureHandling: "none",
                      zoomControl: false,
                    }}
                  >
                    <Marker
                      position={{
                        lat: selectedMeetingLocation?.lat
                          ? parseFloat(selectedMeetingLocation.lat)
                          : parseFloat(userLocation.lat),
                        lng: selectedMeetingLocation?.lng
                          ? parseFloat(selectedMeetingLocation.lng)
                          : parseFloat(userLocation.lng),
                      }}
                    />
                  </GoogleMap>
                ) : (
                  <div className="rab-map-fallback">
                    <Loader2 className="animate-spin" />
                  </div>
                )}
              </div>

              <button
                className={`rab-loc-confirm-btn ${isRescheduling ? "disabled" : ""}`}
                onClick={handleRescheduleSubmit}
                disabled={isRescheduling}
              >
                {isRescheduling ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  "SEND REQUEST"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiveABookPage;
